/**
 * Family Tree of the Week — Home spotlight.
 * Fetches the most-active public tree and displays a strip with member portraits.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { TreeDeciduous, Users, ArrowUpRight } from "lucide-react";

export default function FeaturedFamilyTreeStrip() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/family-trees/featured")
      .then((r) => setData(r.data))
      .catch(() => setData({ tree: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || !data.tree) return null;
  const { tree, members = [] } = data;

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12"
      data-testid="featured-family-tree-strip"
    >
      <div
        className="rounded-3xl border p-6 md:p-8 relative overflow-hidden"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
              <TreeDeciduous size={13} className="inline mr-1.5 -mt-0.5" /> FAMILY TREE OF THE WEEK
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              {tree.name}
            </h2>
            <div className="mt-2 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              {tree.country || "Global"} · {tree.member_count || 0} member{tree.member_count === 1 ? "" : "s"}
            </div>
            {tree.description && (
              <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--wofy-text-soft)" }}>
                {tree.description}
              </p>
            )}
            <Link
              to={`/family/${tree.slug || tree.id}`}
              data-testid="featured-family-tree-link"
              className="mt-5 inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4"
              style={{ color: "var(--wofy-primary)" }}
            >
              Explore this family tree <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {members.length > 0 && (
            <div className="flex -space-x-3">
              {members.slice(0, 6).map((m) => {
                const initial = (m.first_name || "?")[0];
                return (
                  <div
                    key={m.id}
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 overflow-hidden shrink-0"
                    style={{ borderColor: "var(--wofy-paper)", background: "var(--wofy-paper)" }}
                    title={[m.first_name, m.last_name].filter(Boolean).join(" ")}
                  >
                    {m.photo ? (
                      <img src={m.photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-xl font-display"
                        style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}
                      >
                        {initial}
                      </div>
                    )}
                  </div>
                );
              })}
              {tree.member_count > 6 && (
                <div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 flex items-center justify-center text-xs font-mono"
                  style={{
                    borderColor: "var(--wofy-paper)",
                    background: "var(--wofy-primary)",
                    color: "white",
                  }}
                >
                  +{tree.member_count - 6}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="text-xs inline-flex items-center gap-1.5" style={{ color: "var(--wofy-text-soft)" }}>
            <Users className="w-3.5 h-3.5" />
            Featured for their active engagement — comments, albums, and family events.
          </div>
          <Link
            to="/family"
            data-testid="featured-family-tree-cta"
            className="wofy-btn-ghost text-xs"
          >
            Create your own family tree
          </Link>
        </div>
      </div>
    </section>
  );
}
